import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useRequest from '../useRequest';
import {
  GET_ON_BEHALF_USERS,
  GET_ON_BEHALF_USER_BY_ID,
  CREATE_ON_BEHALF_USER,
  UPDATE_ON_BEHALF_USER,
  DELETE_ON_BEHALF_USER,
  GET_ELIGIBLE_USER_IDS,
  GET_AVAILABLE_USERS
} from '../../services/URLs';

// Types
export interface CompanyInfo {
  companyId: number;
  companyName: string;
  companyType: string;
  custNum?: string;
}

export interface OnBehalfUser {
  id: string;
  userId: number;
  userEmail: string;
  firstName: string;
  lastName: string;
  accessLevel: string;
  active: boolean;
  addedBy: string;
  addedAt: string;
  updatedAt: string;
  notes: string;
  admin: boolean;
  // Company restrictions fields
  allowedCompanyIds?: number[];
  restrictionType?: string;
  restrictionsUpdatedAt?: string;
  restrictionsUpdatedBy?: string;
  allowedCompanies?: CompanyInfo[]; // For UI display with customer numbers
}

export interface AvailableUser {
  userId: number;
  userEmail: string;
  firstName: string;
  lastName: string;
  accessLevel: string;
}

export interface CreateOnBehalfUserRequest {
  userId: number;
  userEmail: string;
  firstName?: string;
  lastName?: string;
  accessLevel?: string;
}

export interface UpdateOnBehalfUserRequest {
  firstName?: string;
  lastName?: string;
  accessLevel?: string;
  isActive: boolean;
}

// Hooks
export const useGetOnBehalfUsers = (enabled: boolean = true) => {
  const request = useRequest();
  
  return useQuery({
    queryKey: ['onBehalfUsers'],
    queryFn: async () => {
      try {
        const response = await request({
          method: 'get',
          url: GET_ON_BEHALF_USERS,
          config: {},
          completeResponse: false,
        });
        
        // Handle both array and object responses
        if (Array.isArray(response)) {
          return response as OnBehalfUser[];
        } else if (response && typeof response === 'object' && 'onBehalfUsers' in response) {
          return response.onBehalfUsers as OnBehalfUser[];
        } else {
          // If response is empty or null, return empty array
          return [] as OnBehalfUser[];
        }
      } catch (error) {
        console.error('Error fetching on-behalf users:', error);
        throw error;
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once
  });
};

export const useGetOnBehalfUserById = (id: string) => {
  const request = useRequest();
  
  return useQuery({
    queryKey: ['onBehalfUser', id],
    queryFn: async () => {
      const response = await request({
        method: 'get',
        url: GET_ON_BEHALF_USER_BY_ID(id),
        config: {},
        completeResponse: false,
      });
      return response.data as OnBehalfUser;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateOnBehalfUser = () => {
  const queryClient = useQueryClient();
  const request = useRequest();
  
  return useMutation({
    mutationFn: async (data: CreateOnBehalfUserRequest) => {
      const response = await request({
        method: 'post',
        url: CREATE_ON_BEHALF_USER,
        payload: data,
        config: {},
        completeResponse: false,
      });
      return response.data as OnBehalfUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onBehalfUsers'] });
    },
  });
};

export const useUpdateOnBehalfUser = () => {
  const queryClient = useQueryClient();
  const request = useRequest();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateOnBehalfUserRequest }) => {
      const response = await request({
        method: 'put',
        url: UPDATE_ON_BEHALF_USER(id),
        payload: data,
        config: {},
        completeResponse: false,
      });
      return response.data as OnBehalfUser;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['onBehalfUsers'] });
      queryClient.invalidateQueries({ queryKey: ['onBehalfUser', id] });
    },
  });
};

export const useDeleteOnBehalfUser = () => {
  const queryClient = useQueryClient();
  const request = useRequest();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await request({
        method: 'delete',
        url: DELETE_ON_BEHALF_USER(id),
        config: {},
        completeResponse: false,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onBehalfUsers'] });
    },
  });
};

export const useGetEligibleUserIds = () => {
  const request = useRequest();
  
  return useQuery({
    queryKey: ['eligibleUserIds'],
    queryFn: async () => {
      const response = await request({
        method: 'get',
        url: GET_ELIGIBLE_USER_IDS,
        config: {},
        completeResponse: false,
      });
      return response.data as number[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGetAvailableUsers = () => {
  const request = useRequest();
  
  return useQuery({
    queryKey: ['availableUsers'],
    queryFn: async () => {
      try {
        const response = await request({
          method: 'get',
          url: GET_AVAILABLE_USERS,
          config: {},
          completeResponse: false,
        });
        
        if (Array.isArray(response)) {
          return response as AvailableUser[];
        } else {
          return [] as AvailableUser[];
        }
      } catch (error) {
        console.error('Error fetching available users:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once
  });
}; 